import shelfImage from 'assets/images/shelf.png';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { getUserData } from 'store/selectors/user';
import { useGetAllSurveysQuery } from 'store/services/feedbackGroup';
import { useGetLearningCommunityVolumesQuery } from 'store/services/learningCommunities/volumeChapter';
import DropdownV2 from 'style-guide/DropdownV2';
import { Down } from 'style-guide/Icons';
import Image from 'style-guide/Image';
import { Input } from 'style-guide/Input';
import Text from 'style-guide/Typography/Text';
import RadarChart from 'views/LearningCommunity/Homepage/RadarChart';
import SurveyResultsWrapper from './style/SurveyResultsWrapper';

const DropdownBody = ({ surveysList, onSelect }) => (
  <div className='d-flex flex-column dropdown-body'>
    {surveysList.length ? (
      surveysList.map((survey) => (
        <Text $variant={2} key={survey.id} className='dropdown-item' onClick={() => onSelect(survey)}>
          {survey.name}
        </Text>
      ))
    ) : (
      <Text className='dropdown-item'>Looks like you haven’t attached any surveys yet.</Text>
    )}
  </div>
);

const SurveyResults = () => {
  const user = useSelector(getUserData);
  const { data } = useGetAllSurveysQuery({ userId: user.id, includeCourses: true });
  const { selectedSurvey, setSelectedSurvey } = useOutletContext();
  const [selectedChapter, setSelectedChapter] = useState(null);

  const formattedData = useMemo(() => {
    const formattedDataRow =
      data?.flatMap((survey) =>
        (survey.courses || []).map((course) => ({
          name: course.name,
          id: course.id,
          courseName: course.name,
        }))
      ) || [];
    return formattedDataRow.filter((item, index, arr) => arr.findIndex((v) => v.id === item.id) === index) || [];
  }, [data]);

  const surveyChapterIds = useMemo(() => (data || []).flatMap((survey) => survey.chapterId), [data]);

  const { data: volumesData } = useGetLearningCommunityVolumesQuery(
    { id: selectedSurvey?.id },
    { skip: !selectedSurvey?.id }
  );

  const chapters = useMemo(
    () =>
      volumesData
        ?.filter((el) => !el.isFinal)
        .flatMap((volume) => volume.chapters?.map((item) => ({ id: item.id, name: item.name })) || []),
    [volumesData]
  );

  const filteredChapters = useMemo(
    () => chapters?.filter((chapter) => surveyChapterIds.includes(chapter.id)) || [],
    [chapters, surveyChapterIds]
  );

  useEffect(() => {
    if (filteredChapters?.length && selectedSurvey) {
      setSelectedChapter(filteredChapters?.[0]);
    }
  }, [filteredChapters, selectedSurvey]);

  return (
    <SurveyResultsWrapper>
      <div className='d-flex'>
        <DropdownV2 overlay={<DropdownBody surveysList={formattedData} onSelect={setSelectedSurvey} />}>
          <Input
            readOnly
            value={selectedSurvey ? selectedSurvey.name : 'Select LC'}
            Suffix={Down}
            className='survey-dropdown mr--16'
            size='small'
          />
        </DropdownV2>
        {selectedSurvey && (
          <DropdownV2 overlay={<DropdownBody surveysList={filteredChapters} onSelect={setSelectedChapter} />}>
            <Input
              readOnly
              value={selectedChapter ? selectedChapter.name : 'Select chapter'}
              Suffix={Down}
              className='survey-dropdown'
              size='small'
            />
          </DropdownV2>
        )}
      </div>
      {selectedSurvey ? (
        <RadarChart courseId={selectedSurvey.id} chapter={selectedChapter} />
      ) : (
        <div className='d-flex flex-column align-items-center justify-content-center image-section'>
          <Image src={shelfImage} />
          <Text $variant={2}>Choose a Survey from the dropdown to view results.</Text>
        </div>
      )}
    </SurveyResultsWrapper>
  );
};

export default SurveyResults;
